import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Grid, Snackbar, Alert } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';
import DocumentosFiscalesAlbaran from '../components/DocumentosFiscalesAlbaran';

import { calcularTotalesDTE } from './calculostipodte';
import { obtenerDatosToken } from '../utils/authToken';

import AlbaranHeaderBar from './albaranvista/AlbaranHeaderBar';
import AlbaranCabeceraForm from './albaranvista/AlbaranCabeceraForm';
import AlbaranItemSelector from './albaranvista/AlbaranItemSelector';
import AlbaranDetalleTable from './albaranvista/AlbaranDetalleTable';
import AlbaranResumenPanel from './albaranvista/AlbaranResumenPanel';
import AlbaranAbonosPanel from './albaranvista/AlbaranAbonosPanel';
import AlbaranModales from './albaranvista/AlbaranModales';
import ConsultaPrecios from '../components/ConsultaPrecios'; // ajusta la ruta

const AlbaranDetalleView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const esNuevo = id === 'nuevo';

  // Estados principales
  const [albaran, setAlbaran] = useState(null);
  const [detalles, setDetalles] = useState([]);
  const [abonos, setAbonos] = useState([]);
  const [listasPrecio, setListasPrecio] = useState([]);

  const [openConsumoModal, setOpenConsumoModal] = useState(false);
  const handleOpenConsumo = () => setOpenConsumoModal(true);
  const handleCloseConsumo = () => setOpenConsumoModal(false);

  const [openPrintModal, setOpenPrintModal] = useState(false);
  const [tipoDtePrint, setTipoDtePrint] = useState('01');
  const [aplicaRetencionPrint, setAplicaRetencionPrint] = useState(false);

  // Formulario de cabecera
  const [cabeceraForm, setCabeceraForm] = useState({
    id_paciente: '',
    id_cliente: '',
    id_lista_precio: '',
    observaciones: '',
  });

  // Estados para búsqueda de Cliente / Paciente
  const [pacienteOptions, setPacienteOptions] = useState([]);
  const [loadingPacienteBusqueda, setLoadingPacienteBusqueda] = useState(false);
  const [selectedPaciente, setSelectedPaciente] = useState(null);
  const [openPacienteModal, setOpenPacienteModal] = useState(false);

  // Búsqueda de productos
  const [options, setOptions] = useState([]);
  const [loadingBusqueda, setLoadingBusqueda] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Precios disponibles del ítem según la lista
  const [preciosDisponibles, setPreciosDisponibles] = useState([]);

  // Formulario para ítem
  const [itemForm, setItemForm] = useState({
    cantidad: 1,
    precio_unitario: '',
    porcentaje_descuento: 0,
    monto_descuento: 0,
    id_paciente: '',
    observaciones: '',
  });

  const [mascotasCliente, setMascotasCliente] = useState([]);

  // Diálogos para cambio de estado del Albarán
  const [openFacturarModal, setOpenFacturarModal] = useState(false);
  const [openAnularModal, setOpenAnularModal] = useState(false);
  const [motivoAnulacionAlbaran, setMotivoAnulacionAlbaran] = useState('');

  // Modal para Cerrar Albarán
  const [openCerrarModal, setOpenCerrarModal] = useState(false);
  const [observacionesCierre, setObservacionesCierre] = useState('');

  // Estados Pago / Forma Pago
  const [formasPago, setFormasPago] = useState([]);
  const [idFormaPago, setIdFormaPago] = useState('');

  // retencion
  const [aplicaRetencion, setAplicaRetencion] = useState(false);

  // Estados UI
  const [montoAbono, setMontoAbono] = useState('');
  const [referenciaFactura, setReferenciaFactura] = useState('');
  const [refreshDocsKey, setRefreshDocsKey] = useState(0);
  const [guardando, setGuardando] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', severity: 'success' });
  const [mostrarInfoCliente, setMostrarInfoCliente] = useState(false);

  const [openImportDteModal, setOpenImportDteModal] = useState(false);
  const [openCambioModal, setOpenCambioModal] = useState(false);
  const [tipoDte, setTipoDte] = useState('01');

  // Estados de abono y eliminación
  const [referenciaAbono, setReferenciaAbono] = useState('');
  const [observacionesAbono, setObservacionesAbono] = useState('');
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedAbonoToDelete, setSelectedAbonoToDelete] = useState(null);
  const [motivoEliminacion, setMotivoEliminacion] = useState('');

  const [openPreciosModal, setOpenPreciosModal] = useState(false);

  const showError = (err, fallbackMsg) => {
    const detail = err.response?.data?.detail;
    let msg = fallbackMsg;
    if (typeof detail === 'string') {
      msg = detail;
    } else if (Array.isArray(detail)) {
      msg = detail.map((d) => `${d.loc?.[1] || 'Campo'}: ${d.msg}`).join(', ');
    }
    setSnackbar({ open: true, msg, severity: 'error' });
  };

  useEffect(() => {
    const fetchFormasPago = async () => {
      try {
        const res = await api.get('/formas-pago/');
        const listaFormas = res.data || [];
        setFormasPago(listaFormas);
        if (listaFormas.length > 0) {
          setIdFormaPago(listaFormas[0].id_forma_pago);
        }
      } catch (err) {
        console.error('Error al cargar formas de pago:', err);
      }
    };
    fetchFormasPago();
  }, []);

  useEffect(() => {
    const fetchListas = async () => {
      try {
        const res = await api.get('/listas-precio/');
        setListasPrecio(res.data || []);
      } catch (err) {
        console.error('Error al cargar listas de precio', err);
      }
    };
    fetchListas();
  }, []);

  useEffect(() => {
    if (esNuevo) {
      setAlbaran({ estado_albaran: 'ABIERTO', numero_albaran: 'BORRADOR' });
      setDetalles([]);
      setAbonos([]);
    } else {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const idCliente = selectedPaciente?.id_cliente || selectedPaciente?.cliente_id || cabeceraForm.id_cliente;

    if (!idCliente) {
      setMascotasCliente([]);
      return;
    }

    const fetchMascotasCliente = async () => {
      try {
        // Endpoint ajustado con la ruta real declarada en FastAPI
        const res = await api.get(`/pacientes/por-cliente/${idCliente}`);
        setMascotasCliente(res.data || []);
      } catch (err) {
        console.error('Error al obtener mascotas del cliente:', err);
      }
    };

    fetchMascotasCliente();
  }, [selectedPaciente, cabeceraForm.id_cliente]);

  const loadData = async () => {
    try {
      const [resAlb, resDet, resAbo] = await Promise.all([
        api.get(`/albaranes/${id}`),
        api.get(`/albaranes/${id}/detalles`),
        api.get(`/albaranes/${id}/abonos`),
      ]);

      const albData = resAlb.data;
      setAlbaran(albData);
      setDetalles(resDet.data || []);
      setAbonos(resAbo.data || []);

      setCabeceraForm({
        id_paciente: albData.id_paciente || albData.id_pet || '',
        id_cliente: albData.id_cliente || '',
        id_lista_precio: albData.id_lista_precio || '',
        observaciones: albData.observaciones || '',
      });

      if (albData.paciente || albData.id_paciente || albData.id_pet) {
        const pacienteInfo = albData.paciente || {
          id_paciente: albData.id_paciente || albData.id_pet,
          id_cliente: albData.id_cliente || albData.cliente_id,
          nombre_paciente: albData.nombre_paciente || albData.paciente_nombre,
          nombre_cliente: albData.nombre_cliente || albData.cliente_nombre,
          nombre_raza: albData.nombre_raza,
        };

        setSelectedPaciente(pacienteInfo);
        setPacienteOptions([pacienteInfo]);
      }
    } catch (err) {
      showError(err, 'Error al cargar los datos del albarán');
    }
  };

  const handleReabrirAlbaran = async () => {
    try {
      await api.post(`/albaranes/${id}/reabrir`);
      setSnackbar({ open: true, msg: 'Albarán reabierto con éxito', severity: 'success' });
      loadData();
    } catch (err) {
      showError(err, 'Error al reabrir el albarán');
    }
  };

  const handleSearchPaciente = async (term) => {
    if (!term || term.trim().length < 2) return;
    try {
      setLoadingPacienteBusqueda(true);
      const res = await api.get('/pacientes/buscar', { params: { terminos_busqueda: term.trim() } });
      setPacienteOptions(res.data || []);
    } catch (err) {
      console.error('Error al buscar paciente:', err);
    } finally {
      setLoadingPacienteBusqueda(false);
    }
  };

  const handleSeleccionarPaciente = (paciente) => {
    setSelectedPaciente(paciente);
    if (paciente) {
      setCabeceraForm((prev) => ({
        ...prev,
        id_paciente: paciente.id_paciente,
        id_cliente: paciente.id_cliente || paciente.cliente_id,
      }));
    } else {
      setCabeceraForm((prev) => ({ ...prev, id_paciente: '', id_cliente: '' }));
    }
  };

  const handleConfirmarCerrarAlbaran = async () => {
    try {
      await api.post(`/albaranes/${id}/cerrar`, {
        observaciones: observacionesCierre.trim() || null,
      });
      setSnackbar({ open: true, msg: 'Albarán cerrado correctamente', severity: 'success' });
      setOpenCerrarModal(false);
      setObservacionesCierre('');
      loadData();
    } catch (err) {
      showError(err, 'Error al cerrar el albarán');
    }
  };

  const handleAnularAlbaran = async () => {
    if (!motivoAnulacionAlbaran.trim()) return;
    try {
      await api.post(`/albaranes/${id}/anular`, { motivo: motivoAnulacionAlbaran.trim() });
      setSnackbar({ open: true, msg: 'Albarán anulado correctamente', severity: 'success' });
      setOpenAnularModal(false);
      setMotivoAnulacionAlbaran('');
      loadData();
    } catch (err) {
      showError(err, 'Error al anular el albarán');
    }
  };

  const handleMarcarFacturado = async () => {
    if (!referenciaFactura.trim()) return;
    try {
      await api.post(`/albaranes/${id}/marcar-facturado`, { referencia_factura_externa: referenciaFactura });
      setSnackbar({ open: true, msg: 'Albarán marcado como facturado', severity: 'success' });
      setOpenFacturarModal(false);
      setReferenciaFactura('');
      loadData();
    } catch (err) {
      showError(err, 'Error al facturar el albarán');
    }
  };

  const handleOpenPrintModal = () => {
    if (esNuevo || !id) {
      setSnackbar({ open: true, msg: 'Guarde el albarán antes de imprimir', severity: 'warning' });
      return;
    }
    // Inicializa con el tipo actual del resumen
    setTipoDtePrint(tipoDte || '01');
    setAplicaRetencionPrint(aplicaRetencion || false);
    setOpenPrintModal(true);
  };

  const handleConfirmarImprimir = async () => {
    try {
      const response = await api.get(`/albaranes/${id}/imprimir`, {
        params: {
          tipo_dte: tipoDtePrint,
          aplica_retencion: aplicaRetencionPrint,
        },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Albaran_${albaran?.numero_albaran || id}_${tipoDtePrint}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setOpenPrintModal(false);
      setSnackbar({ open: true, msg: 'PDF generado correctamente', severity: 'success' });
    } catch (err) {
      console.error('Error al generar PDF:', err);
      showError(err, 'Error al generar el PDF del albarán');
    }
  };

  const handleUsarDatosAnteriores = (datosDte) => {
    setCabeceraForm((prev) => ({
      ...prev,
      observaciones: `${prev.observaciones ? prev.observaciones + ' | ' : ''}Facturar a: ${
        datosDte.nombre_receptor || datosDte.facturado_a_nombre_de
      } (NIT/DUI: ${datosDte.nit_receptor || datosDte.dui_receptor || 'N/A'})`,
    }));

    setSnackbar({
      open: true,
      msg: 'Datos de facturación anteriores vinculados a las observaciones',
      severity: 'info',
    });
  };

  const handleSearchCatalogo = async (term, isBarcodeScan = false) => {
    if (!term || term.trim().length < 1) return;
    try {
      setLoadingBusqueda(true);
      const res = await api.get('/catalogos/buscar-items', {
        params: { terminos_busqueda: term.trim() },
      });
      const items = res.data || [];
      setOptions(items);

      if (isBarcodeScan && items.length > 0) {
        const itemEncontrado =
          items.find((i) => i.referencia?.toLowerCase() === term.trim().toLowerCase()) || items[0];

        if (itemEncontrado) {
          handleAgregarOIncrementarItem(itemEncontrado);
        }
      }
    } catch (err) {
      console.error('Error al buscar en catálogo:', err);
    } finally {
      setLoadingBusqueda(false);
    }
  };

  // Al subir un DTE al albarán se registra el abono correspondiente y,
  // si aplica, se marca como facturado automáticamente.
  const handleImportacionDteExito = async (numeroControlObtenido, codigoGeneracionObtenido) => {
    if (!numeroControlObtenido) return;

    try {
      const codGenReq = codigoGeneracionObtenido?.trim().toLowerCase();
      const numCtrlReq = numeroControlObtenido?.trim().toLowerCase();

      // Validar duplicado por UUID. Si el UUID es distinto, permite registrar el abono
      // aunque comparta el mismo número de control con otro registro.
      const yaExisteAbono = abonos.some((ab) => {
        const codGenAbono = ab.codigo_generacion?.trim().toLowerCase();
        const refAbono = ab.referencia_abono?.trim().toLowerCase();

        if (codGenReq && codGenAbono) {
          return codGenReq === codGenAbono; // ← prioritario
        }
        return refAbono === numCtrlReq;
      });

      if (yaExisteAbono) {
        setSnackbar({
          open: true,
          msg: `El DTE (${codigoGeneracionObtenido || numeroControlObtenido}) ya fue registrado en este albarán.`,
          severity: 'warning',
        });
        setOpenImportDteModal(false);
        return;
      }

      if (!idFormaPago) {
        setSnackbar({
          open: true,
          msg: 'Seleccione una Forma de Pago antes de cargar el DTE',
          severity: 'warning',
        });
        return;
      }

      const montoARegistrar =
        parseFloat(montoAbono) > 0 ? parseFloat(montoAbono) : resumenTotales?.saldoPendiente || 0;

      if (montoARegistrar <= 0) {
        setSnackbar({
          open: true,
          msg: 'Ingrese un monto a abonar válido superior a 0',
          severity: 'warning',
        });
        return;
      }

      await api.post(`/albaranes/${id}/abonos`, {
        id_albaran: parseInt(id),
        id_forma_pago: parseInt(idFormaPago),
        monto_abono: montoARegistrar,
        referencia_abono: numeroControlObtenido,
        codigo_generacion: codigoGeneracionObtenido || null,
        observaciones: observacionesAbono?.trim() || 'Abono vinculado a importación DTE',
      });

      setMontoAbono('');
      setObservacionesAbono('');
      setOpenImportDteModal(false);
      setRefreshDocsKey((prev) => prev + 1);

      setSnackbar({
        open: true,
        msg: `Abono de $${montoARegistrar.toFixed(2)} registrado con DTE ${numeroControlObtenido}`,
        severity: 'success',
      });

      loadData();
    } catch (err) {
      console.error('Error al registrar abono tras importar DTE:', err);
      showError(err, 'Error al vincular el abono con el DTE importado');
    }
  };

  const handleAgregarOIncrementarItem = async (itemAProcesar) => {
    const itemTarget = itemAProcesar || selectedItem;
    if (!itemTarget) return;

    const cantAAgregar = parseFloat(itemForm.cantidad) || 1;
    const precioUnit = parseFloat(itemForm.precio_unitario) || itemTarget.costo || 0;
    const pctDesc = parseFloat(itemForm.porcentaje_descuento) || 0;
    const montoDesc = parseFloat(itemForm.monto_descuento) || 0;

    // 1. Obtener ID de la mascota elegida en el selector
    const idPacienteLinea = itemForm.id_paciente || selectedPaciente?.id_paciente || null;
    const notaExtendida = itemForm.observaciones?.trim() || '';

    // 2. Buscar el objeto de la mascota en 'mascotasCliente' para extraer su nombre
    const mascotaElegida = mascotasCliente.find((m) => String(m.id_paciente) === String(idPacienteLinea));
    const nombrePacienteLinea = mascotaElegida
      ? mascotaElegida.nombre_paciente || mascotaElegida.nombre
      : selectedPaciente?.nombre_paciente || selectedPaciente?.nombre || '-';

    const indiceExistente = detalles.findIndex((d) => {
      const mismoArticulo = d.id_catalogo === itemTarget.id_catalogo;
      const mismoPrecio = Math.abs((parseFloat(d.precio_unitario) || 0) - precioUnit) < 0.001;
      const mismoDescuento = Math.abs((parseFloat(d.porcentaje_descuento) || 0) - pctDesc) < 0.001;
      const mismaMascota = String(d.id_paciente) === String(idPacienteLinea);
      return mismoArticulo && mismoPrecio && mismoDescuento && mismaMascota;
    });

    if (indiceExistente !== -1) {
      const detalleExistente = detalles[indiceExistente];
      const nuevaCantidad = parseFloat(detalleExistente.cantidad) + cantAAgregar;
      const nuevoMontoDesc = nuevaCantidad * precioUnit * (pctDesc / 100);

      if (esNuevo) {
        const detallesActualizados = [...detalles];
        detallesActualizados[indiceExistente] = {
          ...detalleExistente,
          cantidad: nuevaCantidad,
          monto_descuento: nuevoMontoDesc,
        };
        setDetalles(detallesActualizados);
      } else {
        try {
          await api.delete(`/albaranes/${id}/detalles/${detalleExistente.id_detalle}`);
          await api.post(`/albaranes/${id}/detalles`, {
            id_albaran: parseInt(id),
            id_catalogo: itemTarget.id_catalogo,
            id_paciente: idPacienteLinea,
            cantidad: nuevaCantidad,
            precio_unitario: precioUnit,
            porcentaje_descuento: pctDesc,
            monto_descuento: nuevoMontoDesc,
            observaciones: itemTarget.descripcion,
          });
          loadData();
        } catch (err) {
          showError(err, 'Error al incrementar cantidad del producto');
        }
      }
    } else {
      // 3. Incluir 'nombre_paciente' dentro de la nueva línea
      const nuevaLinea = {
        id_catalogo: itemTarget.id_catalogo,
        descripcion: itemTarget.descripcion,
        referencia: itemTarget.referencia,
        cantidad: cantAAgregar,
        precio_unitario: precioUnit,
        porcentaje_descuento: pctDesc,
        monto_descuento: montoDesc,
        id_paciente: idPacienteLinea,
        nombre_paciente: nombrePacienteLinea,
        observaciones: notaExtendida,
      };

      if (esNuevo) {
        setDetalles((prev) => [...prev, { ...nuevaLinea, id_detalle: Date.now() }]);
      } else {
        try {
          await api.post(`/albaranes/${id}/detalles`, {
            id_albaran: parseInt(id),
            id_catalogo: nuevaLinea.id_catalogo,
            id_paciente: nuevaLinea.id_paciente,
            cantidad: nuevaLinea.cantidad,
            precio_unitario: nuevaLinea.precio_unitario,
            porcentaje_descuento: nuevaLinea.porcentaje_descuento,
            monto_descuento: nuevaLinea.monto_descuento,
            observaciones: notaExtendida ? `${nuevaLinea.descripcion} | ${notaExtendida}` : nuevaLinea.descripcion,
          });
          loadData();
        } catch (err) {
          showError(err, 'Error al agregar ítem');
        }
      }
    }

    setSelectedItem(null);
    setItemForm({ cantidad: 1, precio_unitario: 0, porcentaje_descuento: 0, monto_descuento: 0, id_paciente: '', observaciones: '' });
  };

  useEffect(() => {
    const fetchPreciosItem = async () => {
      if (!selectedItem) {
        setPreciosDisponibles([]);
        return;
      }

      const idListaActiva = esNuevo ? cabeceraForm.id_lista_precio : albaran?.id_lista_precio;

      try {
        const res = await api.get(`/precios/find/${selectedItem.id_catalogo}`);
        const todosLosPrecios = res.data || [];

        let preciosFiltrados = todosLosPrecios;
        if (idListaActiva) {
          preciosFiltrados = todosLosPrecios.filter((p) => parseInt(p.id_lista_precio) === parseInt(idListaActiva));
        }

        if (preciosFiltrados.length === 0 && selectedItem.costo) {
          preciosFiltrados = [{ id_precio: 'costo_base', precio: selectedItem.costo, etiqueta: 'Precio Base' }];
        }

        setPreciosDisponibles(preciosFiltrados);

        const primerPrecio = preciosFiltrados[0]?.precio || selectedItem.costo || 0;

        // Preservar id_paciente anterior para que no se blanquee al elegir producto
        setItemForm((prev) => ({
          ...prev,
          cantidad: 1,
          precio_unitario: primerPrecio,
          porcentaje_descuento: 0,
          monto_descuento: 0,
        }));
      } catch (e) {
        console.warn('Error recuperando lista de precios del producto.');
        const fallback = selectedItem.costo || 0;
        setPreciosDisponibles([{ id_precio: 'def', precio: fallback, etiqueta: 'Precio Único' }]);

        // Preservar id_paciente en el catch también
        setItemForm((prev) => ({
          ...prev,
          cantidad: 1,
          precio_unitario: fallback,
          porcentaje_descuento: 0,
          monto_descuento: 0,
        }));
      }
    };

    fetchPreciosItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem, cabeceraForm.id_lista_precio, albaran?.id_lista_precio]);

  const handlePrecioChange = (nuevoPrecio) => {
    const precio = parseFloat(nuevoPrecio) || 0;
    const cant = parseFloat(itemForm.cantidad) || 1;
    const pct = parseFloat(itemForm.porcentaje_descuento) || 0;
    const nuevoMontoDesc = cant * precio * (pct / 100);

    setItemForm((prev) => ({ ...prev, precio_unitario: nuevoPrecio, monto_descuento: nuevoMontoDesc.toFixed(2) }));
  };

  const handleCantidadChange = (nuevaCantidad) => {
    const cant = parseFloat(nuevaCantidad) || 0;
    const precio = parseFloat(itemForm.precio_unitario) || 0;
    const pct = parseFloat(itemForm.porcentaje_descuento) || 0;
    const nuevoMontoDesc = cant * precio * (pct / 100);

    setItemForm((prev) => ({ ...prev, cantidad: nuevaCantidad, monto_descuento: nuevoMontoDesc.toFixed(2) }));
  };

  const handlePorcentajeDescChange = (val) => {
    const pct = parseFloat(val) || 0;
    const cant = parseFloat(itemForm.cantidad) || 0;
    const precio = parseFloat(itemForm.precio_unitario) || 0;
    const subtotalBruto = cant * precio;
    const montoDesc = subtotalBruto * (pct / 100);

    setItemForm((prev) => ({ ...prev, porcentaje_descuento: val, monto_descuento: montoDesc.toFixed(2) }));
  };

  const handleMontoDescChange = (val) => {
    const monto = parseFloat(val) || 0;
    const cant = parseFloat(itemForm.cantidad) || 0;
    const precio = parseFloat(itemForm.precio_unitario) || 0;
    const subtotalBruto = cant * precio;
    const pct = subtotalBruto > 0 ? (monto / subtotalBruto) * 100 : 0;

    setItemForm((prev) => ({ ...prev, monto_descuento: val, porcentaje_descuento: pct.toFixed(2) }));
  };

  const handleRegistrarAbono = async () => {
    const monto = parseFloat(montoAbono);
    if (!montoAbono || isNaN(monto) || monto <= 0) {
      setSnackbar({ open: true, msg: 'Ingrese un monto válido', severity: 'warning' });
      return;
    }
    if (!idFormaPago) {
      setSnackbar({ open: true, msg: 'Seleccione una forma de pago', severity: 'warning' });
      return;
    }

    try {
      await api.post(`/albaranes/${id}/abonos`, {
        id_albaran: parseInt(id),
        id_forma_pago: parseInt(idFormaPago),
        monto_abono: monto,
        referencia_abono: referenciaAbono.trim() || null,
        observaciones: observacionesAbono.trim() || null,
      });

      setMontoAbono('');
      setReferenciaAbono('');
      setObservacionesAbono('');
      setSnackbar({ open: true, msg: 'Abono registrado con éxito', severity: 'success' });
      loadData();
    } catch (err) {
      showError(err, 'Error al registrar abono');
    }
  };

  const handleConfirmarAnulacionAbono = async () => {
    if (!motivoEliminacion.trim()) {
      setSnackbar({ open: true, msg: 'Ingrese el motivo de anulación', severity: 'warning' });
      return;
    }

    try {
      const idAbono = selectedAbonoToDelete.id_albaran_abono || selectedAbonoToDelete.id_abono;

      await api.delete(`/albaranes/${id}/abonos/${idAbono}`, {
        params: { motivo: motivoEliminacion.trim() },
      });

      setOpenDeleteModal(false);
      setSelectedAbonoToDelete(null);
      setMotivoEliminacion('');
      setSnackbar({ open: true, msg: 'Abono anulado correctamente', severity: 'success' });
      loadData();
    } catch (err) {
      showError(err, 'Error al anular el abono');
    }
  };

  const resumenTotales = useMemo(() => {
    const { subtotalBruto, totalDescuentos, subtotal, montoIva, montoRetencion1, totalPagar } = calcularTotalesDTE(
      detalles,
      tipoDte,
      aplicaRetencion
    );

    const totalAbonado = abonos.reduce((acc, a) => acc + parseFloat(a.monto_abono || a.monto || 0), 0);
    const saldoPendiente = Math.max(0, totalPagar - totalAbonado);

    return {
      subtotalBruto,
      totalDescuentos,
      baseImponible: subtotal,
      subtotal,
      montoIva,
      montoRetencion1,
      totalGeneral: totalPagar,
      totalAbonado,
      saldoPendiente,
    };
  }, [detalles, abonos, tipoDte, aplicaRetencion]);

  useEffect(() => {
    if (resumenTotales.baseImponible < 100.0 && aplicaRetencion) {
      setAplicaRetencion(false);
    }
  }, [resumenTotales.baseImponible, aplicaRetencion]);

  // Atajo F2 / Ctrl+C → Calculadora de cambio
useEffect(() => {
  const handleKeyDown = (e) => {
    if (e.key === 'F2' || (e.ctrlKey && e.key.toLowerCase() === 'c')) {
      e.preventDefault();
      setOpenCambioModal(true);
    }
  };

  window.addEventListener('keydown', handleKeyDown);
  return () => window.removeEventListener('keydown', handleKeyDown);
}, []);

// Atajo F3 → Consulta de Precios
useEffect(() => {
  const handleKeyDown = (e) => {
    if (e.key === 'F3') {
      e.preventDefault();
      setOpenPreciosModal(true);
    }
  };

  window.addEventListener('keydown', handleKeyDown);
  return () => window.removeEventListener('keydown', handleKeyDown);
}, []);

  const handleGuardarAlbaranCompleto = async () => {
    try {
      setGuardando(true);

      const tokenData = obtenerDatosToken();
      const idSucursal = tokenData?.id_sucursal || tokenData?.sucursal_id || null;
      const idPetActual = selectedPaciente?.id_paciente || (cabeceraForm.id_paciente ? parseInt(cabeceraForm.id_paciente) : null);
      const idClienteActual =
        selectedPaciente?.id_cliente ||
        selectedPaciente?.cliente_id ||
        (cabeceraForm.id_cliente ? parseInt(cabeceraForm.id_cliente) : null);

      const payloadCabecera = {
        ...cabeceraForm,
        id_paciente: idPetActual,
        id_pet: idPetActual,
        id_cliente: idClienteActual,
        id_sucursal: idSucursal,
        id_lista_precio: cabeceraForm.id_lista_precio ? parseInt(cabeceraForm.id_lista_precio) : null,
      };

      const res = await api.post('/albaranes/', payloadCabecera);
      const newId = res.data.id_albaran || res.data.id;

      if (detalles.length > 0) {
        await Promise.all(
          detalles.map((linea) =>
            api.post(`/albaranes/${newId}/detalles`, {
              id_albaran: newId,
              id_catalogo: linea.id_catalogo,
              id_paciente: linea.id_paciente || null,
              cantidad: parseFloat(linea.cantidad),
              precio_unitario: parseFloat(linea.precio_unitario),
              porcentaje_descuento: parseFloat(linea.porcentaje_descuento || 0),
              monto_descuento: parseFloat(linea.monto_descuento || 0),
              observaciones: linea.observaciones ? `${linea.descripcion} | ${linea.observaciones}` : linea.descripcion,
            })
          )
        );
      }

      setSnackbar({ open: true, msg: 'Albarán guardado exitosamente', severity: 'success' });
      navigate(`/albaranes/detalle/${newId}`, { replace: true });
    } catch (err) {
      showError(err, 'Error al guardar el albarán');
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminarLinea = async (idDetalle) => {
    if (esNuevo) {
      setDetalles((prev) => prev.filter((item) => item.id_detalle !== idDetalle));
    } else {
      try {
        await api.delete(`/albaranes/${id}/detalles/${idDetalle}`);
        loadData();
      } catch (err) {
        showError(err, 'Error al eliminar línea');
      }
    }
  };

  if (!albaran) return <Typography sx={{ p: 3 }}>Cargando comprobante...</Typography>;

  const esEditable = esNuevo || albaran.estado_albaran === 'ABIERTO';
  const esCerrado = albaran?.estado_albaran === 'CERRADO';

  const tokenData = obtenerDatosToken();

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <AlbaranHeaderBar
        esNuevo={esNuevo}
        albaran={albaran}
        onVolver={() => navigate('/albaranes')}
        onCerrar={() => setOpenCerrarModal(true)}
        onAnular={() => setOpenAnularModal(true)}
        onReabrir={handleReabrirAlbaran}
        onFacturar={() => setOpenFacturarModal(true)}
        onImprimir={handleOpenPrintModal}
      />

      <AlbaranCabeceraForm
        esEditable={esEditable}
        esNuevo={esNuevo}
        albaran={albaran}
        cabeceraForm={cabeceraForm}
        setCabeceraForm={setCabeceraForm}
        selectedPaciente={selectedPaciente}
        pacienteOptions={pacienteOptions}
        loadingPacienteBusqueda={loadingPacienteBusqueda}
        onSearchPaciente={handleSearchPaciente}
        onSeleccionarPaciente={handleSeleccionarPaciente}
        onAbrirBuscarPaciente={() => setOpenPacienteModal(true)}
        listasPrecio={listasPrecio}
        tipoDte={tipoDte}
        setTipoDte={setTipoDte}
        mostrarInfoCliente={mostrarInfoCliente}
        setMostrarInfoCliente={setMostrarInfoCliente}
        onAbrirConsumo={handleOpenConsumo}
        onUsarDatosAnteriores={handleUsarDatosAnteriores}
      />

      {esEditable && (
        <AlbaranItemSelector
          options={options}
          selectedItem={selectedItem}
          setSelectedItem={setSelectedItem}
          onSearchCatalogo={handleSearchCatalogo}
          loadingBusqueda={loadingBusqueda}
          mascotasCliente={mascotasCliente}
          itemForm={itemForm}
          setItemForm={setItemForm}
          selectedPaciente={selectedPaciente}
          esEditable={esEditable}
          preciosDisponibles={preciosDisponibles}
          onPrecioChange={handlePrecioChange}
          onCantidadChange={handleCantidadChange}
          onPorcentajeDescChange={handlePorcentajeDescChange}
          onMontoDescChange={handleMontoDescChange}
          onAgregarItem={handleAgregarOIncrementarItem}
        />
      )}

      <AlbaranDetalleTable
        detalles={detalles}
        esEditable={esEditable}
        esNuevo={esNuevo}
        tipoDte={tipoDte}
        selectedPaciente={selectedPaciente}
        onEliminarLinea={handleEliminarLinea}
        guardando={guardando}
        onGuardarAlbaran={handleGuardarAlbaranCompleto}
      />

      {/* Totales, Abonos y Acciones */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <AlbaranAbonosPanel
            esNuevo={esNuevo}
            esCerrado={esCerrado}
            albaran={albaran}
            esEditable={esEditable}
            formasPago={formasPago}
            idFormaPago={idFormaPago}
            setIdFormaPago={setIdFormaPago}
            montoAbono={montoAbono}
            setMontoAbono={setMontoAbono}
            referenciaAbono={referenciaAbono}
            setReferenciaAbono={setReferenciaAbono}
            observacionesAbono={observacionesAbono}
            setObservacionesAbono={setObservacionesAbono}
            onRegistrarAbono={handleRegistrarAbono}
            onAbrirImportDte={() => setOpenImportDteModal(true)}
            abonos={abonos}
            onAnularAbono={(ab) => {
              setSelectedAbonoToDelete(ab);
              setOpenDeleteModal(true);
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <AlbaranResumenPanel
            tipoDte={tipoDte}
            resumenTotales={resumenTotales}
            aplicaRetencion={aplicaRetencion}
            setAplicaRetencion={setAplicaRetencion}
            onAbrirCalculadora={() => setOpenCambioModal(true)}
          />
        </Grid>
      </Grid>

      <AlbaranModales
        esNuevo={esNuevo}
        selectedPaciente={selectedPaciente}
        openCambioModal={openCambioModal}
        setOpenCambioModal={setOpenCambioModal}
        saldoPendiente={resumenTotales.saldoPendiente}
        onConfirmarCambio={(recibido) => {
          setMontoAbono(recibido);
          setOpenCambioModal(false);
        }}
        openFacturarModal={openFacturarModal}
        setOpenFacturarModal={setOpenFacturarModal}
        referenciaFactura={referenciaFactura}
        setReferenciaFactura={setReferenciaFactura}
        onMarcarFacturado={handleMarcarFacturado}
        openImportDteModal={openImportDteModal}
        setOpenImportDteModal={setOpenImportDteModal}
        idSucursalDte={albaran?.id_sucursal || tokenData?.id_sucursal || tokenData?.sucursal_id || 1}
        idPacienteDte={albaran?.id_paciente || albaran?.id_pet || selectedPaciente?.id_paciente || null}
        idClienteDte={albaran?.id_cliente || selectedPaciente?.id_cliente || selectedPaciente?.cliente_id || null}
        onImportDteExito={handleImportacionDteExito}
        openAnularModal={openAnularModal}
        setOpenAnularModal={setOpenAnularModal}
        motivoAnulacionAlbaran={motivoAnulacionAlbaran}
        setMotivoAnulacionAlbaran={setMotivoAnulacionAlbaran}
        onAnularAlbaran={handleAnularAlbaran}
        openPrintModal={openPrintModal}
        setOpenPrintModal={setOpenPrintModal}
        tipoDtePrint={tipoDtePrint}
        setTipoDtePrint={setTipoDtePrint}
        aplicaRetencionPrint={aplicaRetencionPrint}
        setAplicaRetencionPrint={setAplicaRetencionPrint}
        onConfirmarImprimir={handleConfirmarImprimir}
        openCerrarModal={openCerrarModal}
        setOpenCerrarModal={setOpenCerrarModal}
        observacionesCierre={observacionesCierre}
        setObservacionesCierre={setObservacionesCierre}
        onConfirmarCerrar={handleConfirmarCerrarAlbaran}
        openConsumoModal={openConsumoModal}
        onCerrarConsumo={handleCloseConsumo}
        openDeleteModal={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
        motivoEliminacion={motivoEliminacion}
        setMotivoEliminacion={setMotivoEliminacion}
        onConfirmarAnulacionAbono={handleConfirmarAnulacionAbono}
        openPacienteModal={openPacienteModal}
        setOpenPacienteModal={setOpenPacienteModal}
        onSeleccionarPacienteModal={(pacienteSeleccionado) => {
          setSelectedPaciente(pacienteSeleccionado);
          setCabeceraForm((prev) => ({
            ...prev,
            id_paciente: pacienteSeleccionado.id_paciente,
            id_cliente: pacienteSeleccionado.id_cliente || pacienteSeleccionado.cliente_id,
          }));
          setOpenPacienteModal(false);
        }}
      />

      {/* Sección Documentos Fiscales Vinculados */}
      {(albaran?.estado_albaran === 'FACTURADO' || albaran?.estado_albaran === 'CERRADO') && (
        <DocumentosFiscalesAlbaran
          key={refreshDocsKey}
          idAlbaran={albaran?.id_albaran || id}
          idCliente={albaran?.id_cliente || selectedPaciente?.id_cliente || selectedPaciente?.cliente_id}
          onDocumentDeleted={loadData}
        />
      )}

      {/* Modal Consulta de Precios */}
      <ConsultaPrecios
        open={openPreciosModal}
        onClose={() => setOpenPreciosModal(false)}
      />

      

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AlbaranDetalleView;
